import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why do most dividends land in March, June, September, December?',
    a: 'Because most US companies follow a calendar fiscal year (Jan-Dec) and pay quarterly dividends after the close of each quarter. Q1 results land in February, the board declares the next dividend, and it pays in March. Same cadence for June (Q2), September (Q3), and December (Q4). About 70% of S&P 500 dividend dollars flow through those four months as a result.',
  },
  {
    q: 'What about the "lean" months — Jan, April, July, October?',
    a: 'They\'re only lean if your portfolio is mostly quarterly-payer blue chips on the calendar schedule. Plenty of names DO pay in those months — including most monthly payers (O, MAIN, STAG, AGNC), some quarterly payers with offset schedules (KO actually pays in April / July / October / December — not the Mar/Jun/Sep/Dec calendar), and many REITs and BDCs.',
  },
  {
    q: 'Does DiviDrip show me which months I\'m short?',
    a: 'Yes. The 12-Month Income Heatmap on the Portfolio page colors every month from light to dark violet based on projected dividend income. Weak months (under 60% of your monthly average) get a yellow/amber border around the cell. Strong months (over 130%) get a green/emerald border. A summary line below the grid totals the count of weak and strong months with matching icons so you can see at a glance which months need attention.',
  },
  {
    q: 'How do I balance my calendar?',
    a: 'Add monthly payers to fill the gaps. The most popular monthly dividend stocks (O, MAIN, STAG, AGNC) all pay every month. Adding a few monthly names to a quarterly-heavy portfolio smooths out the cash flow without sacrificing quality. The Monthly Dividend Calendar guide has the full beginner approach.',
  },
  {
    q: 'Can DiviDrip remind me before an ex-dividend date?',
    a: 'Yes. Both the Portfolio page and the Dividend Calendar page have an "Alerts" button (violet, with a bell icon) in the header. Open it to pick your lead-time windows — 5 days, 3 days, and/or 1 day before the ex-date — separately for your portfolio and your watchlist. On your first login each day, DiviDrip pops up a modal listing every ticker whose ex-date falls inside one of your chosen windows, so you don\'t have to remember to check.',
  },
  {
    q: 'Can I see watchlist tickers on the Calendar and Heatmap too?',
    a: 'Yes. Both the dashboard 12-Month Income Heatmap and the full Dividend Calendar page have a scope toggle that flips between "Portfolio only" and "Portfolio + Watchlist". The two surfaces stay in lockstep — flipping the toggle on one updates the other. Useful for checking whether a watchlist name would fill a weak month before you commit to buying it.',
  },
];

export default function LearnDividendCalendarMonths() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Which Months Pay the Most? The Dividend Calendar Breakdown | DiviDrip"
      description="Roughly 70% of US dividend dollars land in just four months — March, June, September, December. The lean months retirees plan for, and how DiviDrip's 12-Month Income Heatmap + Dividend Calendar makes the pattern visible."
      canonical="https://twylightcrow.com/learn/dividend-calendar-months"
      guidePath="/learn/dividend-calendar-months"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Cash-flow planning"
      h1="Which Months Pay the Most? The Dividend Calendar Breakdown"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/monthly-dividend-calendar', label: 'The Monthly Dividend Calendar trick' },
        { to: '/learn/build-5k-monthly-income', label: 'How to build a $5K monthly dividend income' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers in a dividend portfolio' },
        { to: '/learn/ex-dividend-date-timeline', label: 'What happens on the ex-dividend date' },
        { to: '/learn/monthly-vs-quarterly-dividends', label: 'Monthly vs quarterly dividends' },
      ]}
    >
      <p>
        If you charted every US dividend payment on a calendar, the chart would
        look like a four-spike comb. <strong>March, June, September, December</strong>{' '}
        get most of the action. January, April, July, October are the quiet
        months. This isn&apos;t random — it&apos;s a direct consequence of how
        American companies report earnings and how their boards schedule
        dividends.
      </p>
      <p>
        Understanding the pattern matters because dividend income flowing in
        big lumps every three months isn&apos;t how retirees actually live —
        bills come monthly. Let&apos;s look at the structure and then how to
        flatten it.
      </p>

      <h2>The 4-spike pattern</h2>
      <p>
        Roughly 70% of S&amp;P 500 dividend dollars land in just four months:
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Month</th><th>Major payers</th><th>Why these months</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>March</strong></td>
            <td>JNJ, PG, JPM, MSFT, AAPL, HD, MA, V</td>
            <td>Q4 earnings report in late Jan → board declares → pays in March</td>
          </tr>
          <tr>
            <td><strong>June</strong></td>
            <td>JNJ, PG, JPM, MSFT, AAPL, HD, MA, V</td>
            <td>Q1 earnings in April → declared in May → pays in June</td>
          </tr>
          <tr>
            <td><strong>September</strong></td>
            <td>JNJ, PG, JPM, MSFT, AAPL, HD, MA, V</td>
            <td>Q2 earnings in July → declared in August → pays in September</td>
          </tr>
          <tr>
            <td><strong>December</strong></td>
            <td>JNJ, PG, JPM, MSFT, AAPL, HD, MA, V</td>
            <td>Q3 earnings in October → declared in November → pays in December</td>
          </tr>
        </tbody>
      </table>
      <p>
        The result: a portfolio of pure blue-chip dividend payers like KO, JNJ,
        PG, JPM, MSFT will see 4 big months and 8 quieter ones. Total dividends
        are the same, but the cash flow rhythm is bumpy.
      </p>

      <h2>The &quot;quiet&quot; months aren&apos;t actually empty</h2>
      <p>
        Even in January / April / July / October, dividends are still flowing —
        just from a different mix:
      </p>
      <ul>
        <li>
          <strong>Monthly payers.</strong> O (Realty Income), MAIN (Main Street
          Capital), STAG (industrial REIT), AGNC (mREIT), PFFA (preferred ETF),
          JEPI / JEPQ (covered-call ETFs) — all pay every single month. Adding a
          few to a quarterly-heavy portfolio instantly flattens the calendar.
        </li>
        <li>
          <strong>Offset quarterly schedules.</strong> Coca-Cola (KO) actually
          pays in <strong>April, July, October, December</strong> — not the
          standard March/June/September/December cadence. McDonald&apos;s (MCD)
          pays March/June/September/December. AT&amp;T (T) pays February / May /
          August / November. There&apos;s real diversity in the cadences if you
          look.
        </li>
        <li>
          <strong>REITs and BDCs.</strong> Many REITs and most BDCs follow
          quarterly schedules but offset from the calendar. ARCC pays March /
          June / September / December BUT also adds occasional supplemental
          dividends in off-months.
        </li>
      </ul>

      <h2>How to see your own calendar in DiviDrip</h2>
      <p>The site gives you two views, both powered by your actual holdings:</p>

      <h3>1. The 12-Month Income Heatmap (Portfolio page)</h3>
      <p>
        A grid of 12 cells, one per month, colored from light to dark violet
        based on projected dividend income. Weak months (under 60% of your
        monthly average) get a yellow/amber border. Strong months (over 130%)
        get a green/emerald border. A summary line below the grid totals the
        count of weak and strong months with matching icons — so your bumpy
        cash flow is visible at a glance, along with exactly which months need
        filling.
      </p>

      <h3>2. The Dividend Calendar (full page)</h3>
      <p>
        A detailed month-by-month view of every upcoming ex-date and pay date
        across your portfolio + watchlist.
      </p>

      <h3>3. Ex-Dividend Alert pop-ups</h3>
      <p>
        Both the Portfolio page and the full Dividend Calendar page have an{' '}
        <strong>Alerts</strong> button (violet, with a bell icon) in the page
        header. Click it to open the Ex-Div Alert settings panel, where you pick
        how many days before each ex-date you want a heads-up:{' '}
        <strong>5 days, 3 days, or 1 day</strong> before (any combination).
        Then on your first login each day, DiviDrip pops up a modal listing
        every ticker whose ex-date falls inside one of your chosen windows —
        so you don&apos;t need to remember to check. The panel covers your
        portfolio and your watchlist separately, so you can opt in to one,
        the other, or both.
      </p>

      <h3>4. Portfolio-only vs. Portfolio + Watchlist toggle</h3>
      <p>
        Next to the Dividend Calendar title (and on the dashboard heatmap)
        sits a small <strong>scope toggle</strong>. Flip it to{' '}
        <strong>&quot;Portfolio only&quot;</strong> to see just the stocks you
        actually own, or to <strong>&quot;Portfolio + Watchlist&quot;</strong>{' '}
        to fold in the watchlist tickers too — useful when you&apos;re
        evaluating whether a watchlist name would fill a weak month before
        you commit to buying it. Both surfaces stay in lockstep, so flipping
        the toggle anywhere updates everywhere.
      </p>

      <h2>The 3-step &ldquo;flatten my calendar&rdquo; workflow</h2>
      <ol>
        <li>
          <strong>Identify your weak months</strong> on the Heatmap. If you see
          yellow/amber borders on January, April, July, and October — your
          calendar is the textbook 4-spike pattern.
        </li>
        <li>
          <strong>Add 2-3 monthly payers</strong> to fill the gaps. O, MAIN,
          STAG, JEPI, JEPQ are popular starting points. Even modest positions in
          these instantly add income to every month including the lean ones.
        </li>
        <li>
          <strong>Layer in offset quarterly payers</strong> for additional
          balance. If you already own KO (April / July / October / December
          cycle), MCD (March / June / September / December), and AT&amp;T (Feb /
          May / Aug / Nov), the spread covers every single month.
        </li>
      </ol>

      <h2>The retiree&apos;s view</h2>
      <p>
        For someone living off dividend income, the calendar shape matters more
        than the absolute total. A $60,000/year dividend portfolio that pays
        $20,000 in December and $1,000 in October is much harder to budget than
        one that pays $5,000 every month. Hitting that smoother shape is exactly
        what the Heatmap optimizes for — it&apos;s why DiviDrip surfaces it on
        the Portfolio page rather than hiding it in a sub-menu.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, head to the Portfolio page,
        and scroll to the 12-Month Income Heatmap. If you have a quarterly-heavy
        portfolio, you&apos;ll see the spike pattern immediately.
      </p>
    </LearnPage>
  );
}
